import { useRef, useEffect, useState } from 'react';
import {
  AppReactECharts,
  AutoCompleteController,
  DatePickerController,
  InputController,
} from '../../../../components';
import { AppCircularProgress } from '../../../../components/common/loaders/AppCircularProgress';
import { MVPTable } from '../table';
import * as yup from 'yup';
import { useFormWithSchema } from '../../../../utils/functions/yupFormTypescript';
import dayjs from 'dayjs';
import {
  Box,
  Drawer,
  Grid,
  Button,
  Typography,
  Switch,
  FormControlLabel,
} from '@mui/material';
import { useGetFilterDataForMapQuery } from '../../../../services/mVPMapService';
import { useGetScatterPlotDataQuery } from '../../../../services/mVPGraphQlService';

import { MVPFiltersProps } from '../../constants';
import { useFilterContext } from '../../../../services/mVPMapService/filterContext';
import { useSelector } from 'react-redux';
import { RootState } from '../../../../store';
import React from 'react';
import { useDebounce } from '../../../../services/customHook/debounceHook';
import { useAlertHelper } from '../../../../utils/functions/hooks';
import { BoxplotChart } from '../boxPlotChart';
import FilterAltOutlinedIcon from '@mui/icons-material/FilterAltOutlined';
import { MVPFilters } from '../../filters';

import AppsOutlinedIcon from '@mui/icons-material/AppsOutlined';
import CommonAppBar from '../../../../components/commonAppBar';
const formSchema = yup.object({
  dateOfData: yup.date().nullable(),
  date: yup.string().default('CUSTOM_DATE'),
  intValue: yup
    .number()
    .min(0, 'Number must be at least 0')
    .max(200, 'Number cannot exceed 200')
    .typeError('Only numbers are allowed')
    .default(0),
});
const drawerWidth = 360;
export const ScatterplotChart: React.FC<
  MVPFiltersProps
> = ({ handleApplyFilter, onNavigate }) => {
  const isInitialRender = useRef(true);
  const { showError } = useAlertHelper();
  const {
    control,
    formState: { errors },
    watch,
    setValue,
    register,
  } = useFormWithSchema(formSchema, {
    mode: 'onTouched',
    defaultValues: {
      dateOfData: null,
    },
  });
  const { filterValues: commonFilters } =
    useFilterContext();
  const wsId = useSelector(
    (state: RootState) => state.country.wsId
  );
  const [showGraphTable, setShowGraphTable] =
    useState(false);

  const handleSwitchChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setShowGraphTable(event.target.checked);
  };

  const [currentMonth, setCurrentMonth] = useState(() => {
    const now = new Date();
    const month = now.getMonth() + 1; // getMonth() is 0-indexed
    const year = now.getFullYear();
    return `${year}-${month < 10 ? '0' + month : month}`;
  });

  const handleVisibleMonthChange = (date: Date) => {
    const month = date.getMonth() + 1;
    const year = date.getFullYear();
    setCurrentMonth(
      `${year}-${month < 10 ? '0' + month : month}`
    );
  };

  const baseDate = dayjs(
    `${currentMonth}-01`,
    'MMMM YYYY-DD'
  ); // Ensure valid date in Safari

  const startOfPreviousMonth = baseDate
    .subtract(1, 'month')
    .startOf('month')
    .unix();

  const endOfNextMonth = baseDate
    .add(1, 'month')
    .endOf('month')
    .unix();

  const {
    data: filtersDataObject,
    isFetching: isFetchingDates,
  } = useGetFilterDataForMapQuery(
    {
      commonFilters,
      wsId,
      startTime: startOfPreviousMonth,
      endTime: endOfNextMonth,
      filterTypes: ['FILTER_DATE'],
    },
    {
      skip: !commonFilters?.index || !commonFilters?.season,
    }
  );

  const includeDatesList =
    filtersDataObject?.data?.GetDataForTimeRangeWithFilters?.dates?.map(
      (item) => new Date(item)
    );

  useEffect(() => {
    if (includeDatesList?.length) {
      const selectedDate = watch('dateOfData');

      const selectedDateTimeUTC = selectedDate
        ? Date.UTC(
            selectedDate.getFullYear(),
            selectedDate.getMonth(),
            selectedDate.getDate()
          )
        : null;

      const includeDatesUTC = includeDatesList
        .filter((date) => date instanceof Date)
        .map((date) =>
          Date.UTC(
            date.getFullYear(),
            date.getMonth(),
            date.getDate()
          )
        );

      const isDateValid =
        selectedDateTimeUTC !== null &&
        includeDatesUTC.includes(selectedDateTimeUTC);

      if (isInitialRender.current) {
        setValue('dateOfData', includeDatesList[0], {
          shouldValidate: true,
        });
        isInitialRender.current = false;
      } else if (!isDateValid) {
        setValue('dateOfData', null, {
          shouldValidate: true,
        });
      } else {
        console.log(
          'Date is valid - Keeping existing value'
        );
      }
    } else {
      setValue('dateOfData', null, {
        shouldValidate: true,
      });
    }
  }, [filtersDataObject]);

  const selectedDate = dayjs(watch('dateOfData'));
  const startTime = selectedDate.isValid()
    ? Math.floor(
        Number(selectedDate.startOf('day').utc()) / 1000
      )
    : undefined; // Use undefined instead of an empty string.

  const endTime = selectedDate.isValid()
    ? Math.floor(
        Number(selectedDate.endOf('day').utc()) / 1000
      )
    : undefined;

  const debouncedIntValue = useDebounce(
    Number(watch('intValue')),
    1000
  );

  const shouldFetchData =
    typeof debouncedIntValue === 'number' &&
    debouncedIntValue <= 200;

  const { data: plotData, isFetching: isLoadingPlotData } =
    useGetScatterPlotDataQuery(
      {
        commonFilters,
        wsId,
        startTime: startTime || 0,
        endTime: endTime || 0,
        tableDataType: watch('date'),
        daysAfterPlanting: debouncedIntValue,
      },
      {
        skip:
          !shouldFetchData ||
          (watch('date') == 'CUSTOM_DATE' &&
            !startTime &&
            !endTime),
      }
    );

  useEffect(() => {
    if (debouncedIntValue > 200) {
      showError('Number of days cannot exceed 200');
      return;
    }
  }, [debouncedIntValue]);

  const xAxisData =
    plotData?.data?.getScatterPlotDetailsForDate?.xAxis;

  const yAxisData =
    plotData?.data?.getScatterPlotDetailsForDate?.yAxis;

  const noDataFieldNos =
    plotData?.data?.getScatterPlotDetailsForDate
      ?.noDataFieldNos;

  const options = {
    xAxis: {
      name: 'Field ID',
      nameTextStyle: {
        fontWeight: 'bolder',
        fontSize: '14',
      },
      data: xAxisData,
    },
    yAxis: {
      name: commonFilters?.index,
      nameTextStyle: {
        fontWeight: 'bolder',
        fontSize: '14',
      },
    },
    series: [
      {
        data: yAxisData,
        type: 'scatter',
      },
    ],
    tooltip: {
      trigger: 'axis',
    },
    dataZoom: [
      {
        type: 'slider',
        xAxisIndex: 0,
        filterMode: 'none',
        minValueSpan: 5,
      },
    ],
  };
  const dateFieldOptions = [
    {
      value: 'CUSTOM_DATE',
      label: 'Date',
    },
    {
      value: 'DAY_AFTER_PLANTING_TYPE',
      label: 'Days after Planting',
    },
  ];
  const [filterOpen, setFilterOpen] = useState(false);

  const toggleFilter = () => setFilterOpen((prev) => !prev);
  return (
    <>
      <CommonAppBar
        // title="Insights"
        // showFilter={true}
        // onFilterClick={toggleFilter}
        section={
          <>
            <Grid
              container
              alignItems="center"
              spacing={2}
              sx={{ flexWrap: 'nowrap' }}>
              <Grid item>
                <Typography fontSize={15} fontWeight={600}>
                  Insights
                </Typography>
              </Grid>

              <Grid item>
                <Button
                  onClick={toggleFilter}
                  variant="outlined"
                  startIcon={<FilterAltOutlinedIcon />}
                  sx={{
                    textTransform: 'none',
                    borderColor: '#ccc',
                    color: '#333',
                    fontSize: 14,
                    background: '#fff',
                    '&:hover': {
                      borderColor: '#888',
                      backgroundColor: '#fafafa',
                    },
                  }}>
                  Filter
                </Button>
              </Grid>

              <Grid
                item
                xs={6}
                sm={6}
                md={1.5}
                lg={1.5}
                className="field-grid"
                style={{ marginTop: 75 }}>
                <AutoCompleteController
                  label=""
                  name="date"
                  control={control}
                  options={dateFieldOptions}
                  error={errors.date?.message}
                  disableClearable
                  height={38}
                />
              </Grid>

              {watch('date') === 'CUSTOM_DATE' && (
                <Grid
                  item
                  xs={6}
                  sm={6}
                  md={1.5}
                  lg={1.5}
                  className="field-grid"
                  style={{ marginTop: 70 }}>
                  <DatePickerController
                    label=""
                    control={control}
                    required
                    name="dateOfData"
                    error={errors?.dateOfData?.message}
                    includeDates={includeDatesList}
                    onVisibleMonthChange={
                      handleVisibleMonthChange
                    }
                  />
                </Grid>
              )}

              {watch('date') ===
                'DAY_AFTER_PLANTING_TYPE' && (
                <Grid
                  item
                  xs={6}
                  sm={6}
                  md={1.5}
                  lg={1.5}
                  className="field-grid"
                  style={{ marginTop: 48 }}>
                  <InputController
                    label=""
                    register={register('intValue')}
                    type="number"
                    required
                    error={errors.intValue?.message}
                    inputHeight={'40px'}
                  />
                </Grid>
              )}

              <Grid item>
                <FormControlLabel
                  control={
                    <Switch
                      size="small"
                      checked={showGraphTable}
                      onChange={handleSwitchChange}
                    />
                  }
                  label="Show Graph Table"
                  sx={{
                    ml: 1,
                    '& .MuiFormControlLabel-label': {
                      fontSize: 14,
                      color: '#333',
                    },
                  }}
                />
              </Grid>
            </Grid>

            <Box>
              <Button
                variant="outlined"
                startIcon={<AppsOutlinedIcon />}
                sx={{
                  width: 200,
                  height: 40,
                  textTransform: 'none',
                  borderColor: '#0A7C6E',
                  color: '#0A7C6E',
                  fontWeight: 600,
                  borderWidth: 1.5,
                  '&:hover': {
                    borderColor: '#0A7C6E',
                    // backgroundColor: 'rgba(0, 105, 112, 1)',
                  },
                }}
                onClick={() => onNavigate?.('comparative')}>
                Compare Fields
              </Button>
            </Box>
          </>
        }
      />

      <div
        style={{
          padding: 20,
          backgroundColor: '#ececec',
          marginTop: 70,
        }}>
        <div
          style={{
            backgroundColor: 'white',
            borderRadius: 8,
            padding: 20,
          }}>
          {isFetchingDates || isLoadingPlotData ? (
            <AppCircularProgress />
          ) : (
            <div>
              <Typography
                variant="subtitle1"
                sx={{ fontWeight: 600 }}>
                Scatter Plot{' '}
                <Typography
                  component="span"
                  variant="body2"
                  sx={{ fontWeight: 400 }}>
                  [
                  {`${
                    noDataFieldNos?.length || 0
                  } farms excluded on selected date (out of ${
                    (xAxisData?.length || 0) +
                    (noDataFieldNos?.length || 0)
                  })`}
                  ]
                </Typography>
              </Typography>
              {/* 
              xAxisData?.length ? (
                <AppReactECharts
                  option={options}
                  startTime={startTime}
                  endTime={endTime}
                  commonFilters={commonFilters}
                  plotType="scatter"
                  onNavigate={onNavigate}
                />
              ) : (
                <Box className="no-data-text text-center">
                  {watch('date') == 'CUSTOM_DATE' &&
                  includeDatesList &&
                  includeDatesList?.length > 0
                    ? 'Please select the date'
                    : ' No Data Found In Graph'}
                </Box>
              )}
               */}
              {watch('date') == 'CUSTOM_DATE' &&
              includeDatesList &&
              includeDatesList?.length > 0 ? (
                xAxisData?.length ? (
                  <AppReactECharts
                    option={options}
                    startTime={startTime}
                    endTime={endTime}
                    commonFilters={commonFilters}
                    plotType="scatter"
                    onNavigate={onNavigate}
                  />
                ) : (
                  <Box className="no-data-text text-center">
                    Please select the date
                  </Box>
                )
              ) : watch('date') ==
                  'DAY_AFTER_PLANTING_TYPE' &&
                xAxisData?.length ? (
                <AppReactECharts
                  option={options}
                  startTime={startTime}
                  endTime={endTime}
                  commonFilters={commonFilters}
                  plotType="scatter"
                  onNavigate={onNavigate}
                />
              ) : (
                <Box className="no-data-text text-center">
                  No Data Found In Graph
                </Box>
              )}
            </div>
          )}
        </div>
        <div
          style={{
            backgroundColor: 'white',
            borderRadius: 8,
            padding: 20,
            marginTop: 10,
          }}>
          <BoxplotChart
            dateOfData={watch('dateOfData')}
            date={watch('date')}
            intValue={watch('intValue')}
            includeDatesList={includeDatesList}
          />
        </div>
        {showGraphTable && (
          <div
            style={{
              backgroundColor: 'white',
              borderRadius: 8,
              padding: 20,
              marginTop: 10,
            }}>
            <MVPTable
              fieldNumbers={noDataFieldNos}
              startTime={
                xAxisData?.length ? startTime : undefined
              }
              endTime={
                xAxisData?.length ? endTime : undefined
              }
              commonFilters={commonFilters}
              type={'SCATTERPLOT_TAB'}
              dataType={watch('date')}
              daysAfterPlantingValue={debouncedIntValue}
              onNavigate={onNavigate}
            />
          </div>
        )}
      </div>
      <Drawer
        variant="persistent"
        anchor="left"
        open={filterOpen}
        onClose={toggleFilter}
        ModalProps={{
          keepMounted: true,
        }}
        sx={{
          [`& .MuiDrawer-paper`]: {
            width: drawerWidth,
            boxSizing: 'border-box',
            p: 2,
            top: 118, // match AppBar height
            left: 60, // open from sidebar edge
            height: 'calc(100% - 64px)', // fill remaining height below header
            borderTop: '1px solid #eee', // subtle divider (optional)
          },
        }}>
        <MVPFilters
          filterValues={commonFilters}
          //   filtersIsCollapsed={filtersIsCollapsed}
          handleApplyFilter={handleApplyFilter}
          setFilterOpen={setFilterOpen}
        />
      </Drawer>
    </>
  );
};
